/* Mida v1.0. Publishes only to Shopify; external destinations live in Customer events. */
(() => {
  'use strict';
  if (window.Shopify?.designMode || window.__midaTrackingBridge) return;
  window.__midaTrackingBridge = true;
  const storageKey = 'mida:pending-form:v1';
  const maxAge = 30 * 60 * 1000;
  let lastClick = {method: '', at: 0};

  function allowed() {
    const p = window.Shopify?.customerPrivacy;
    return p?.analyticsProcessingAllowed?.() === true ||
      (p?.marketingAllowed?.() === true && p?.saleOfDataAllowed?.() === true);
  }

  function publish(name, values) {
    if (!allowed() || typeof window.Shopify?.analytics?.publish !== 'function') return false;
    try { window.Shopify.analytics.publish(name, values); return true; }
    catch { return false; }
  }

  function formDetails(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    const type = form.querySelector('input[name="form_type"]')?.value;
    if (!['contact', 'customer'].includes(type)) return null;
    const formId = form.id || '';
    // Only predefined metadata and the public page path are inspected. Never field values.
    const isOutlet = formId.startsWith('MidaOutletNewsletter-');
    const isNewsletter = type === 'customer';
    let leadType = 'contact';
    if (formId === 'MidaSofaQuoteForm' || /^(ChairContact|LivingContact)-/.test(formId)) leadType = 'product_configuration';
    else if (formId === 'MidaBookingForm') {
      leadType = /progett|cucin/.test(location.pathname) ? 'design_consultation' : 'showroom_appointment';
    }
    const siblings = Array.from(document.forms).filter(f =>
      (f.id || '') === formId && f.querySelector('input[name="form_type"]')?.value === type);
    return {key: location.pathname + '|' + type + '|' + formId + '|' + siblings.indexOf(form),
      event: isNewsletter ? 'mida:newsletter_signup' : 'mida:lead_submitted',
      detail: isNewsletter ? {newsletter_type: isOutlet ? 'outlet' : 'general'} : {lead_type: leadType}};
  }

  function success(form) {
    // All selectors correspond to success branches verified in the current live theme.
    if (form.querySelector('.contact-form__error, [role="alert"], .errors, [aria-invalid="true"]')) return false;
    const positive = form.querySelector('.contact-form__success, .mida-booking-success, [id^="Email-signup__message-success-"]');
    if (positive) return true;
    if (/^(ChairContact-|LivingContact-|MidaOutletNewsletter-)/.test(form.id || '')) {
      return !!form.querySelector('[role="status"]');
    }
    return false;
  }

  function verifySuccess() {
    if (!allowed()) return;
    let pending;
    try { pending = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { return; }
    if (!pending || !Number.isFinite(pending.at) || Date.now() - pending.at > maxAge) {
      try { sessionStorage.removeItem(storageKey); } catch {}
      return;
    }
    for (const form of document.forms) {
      const details = formDetails(form);
      if (!details || details.key !== pending.key || !success(form)) continue;
      if (publish(details.event, {...details.detail, submission_id: pending.id})) {
        try { sessionStorage.removeItem(storageKey); } catch {}
      }
      break;
    }
  }

  document.addEventListener('submit', event => {
    const details = formDetails(event.target);
    if (!details || !allowed() || !event.target.checkValidity()) return;
    // A local random nonce proves an actual submit preceded the success screen.
    // It contains no name, email, telephone, message or customer identifier.
    try {
      const nonce = crypto.randomUUID();
      sessionStorage.setItem(storageKey, JSON.stringify({key: details.key, at: Date.now(), id: nonce}));
    } catch { /* No conversion is inferred if storage is unavailable. */ }
  }, true);

  document.addEventListener('click', event => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    // Reopen Shopify's native choices without changing consent ourselves.
    if (link.hasAttribute('data-mida-cookie-preferences')) {
      if (typeof window.privacyBanner?.showPreferences === 'function') {
        event.preventDefault();
        Promise.resolve(window.privacyBanner.showPreferences()).catch(() => {
          location.assign(link.href);
        });
      }
      return;
    }
    if (!allowed()) return;
    let method = '';
    try {
      const url = new URL(link.getAttribute('href'), location.origin);
      if (url.protocol === 'tel:') method = 'telephone';
      else if (url.protocol === 'mailto:') method = 'email';
      else if (url.hostname === 'wa.me' || url.hostname === 'api.whatsapp.com') method = 'whatsapp';
    } catch { return; }
    if (!method || (method === lastClick.method && Date.now() - lastClick.at < 1500)) return;
    lastClick = {method, at: Date.now()};
    publish('mida:contact_click', {contact_method: method}); // Do not export href / message.
  }, true);

  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) { try { sessionStorage.removeItem(storageKey); } catch {} }
    else verifySuccess();
  });
  document.addEventListener('shopify:section:load', verifySuccess);
  window.addEventListener('pageshow', verifySuccess);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', verifySuccess, {once: true});
  else verifySuccess();
  // Consent and Shopify analytics may initialize shortly after deferred theme scripts.
  for (const delay of [500, 1500, 4000]) window.setTimeout(verifySuccess, delay);
})();
